/**
 * Medical History Integration Service
 * 
 * Handles automatic synchronization of encounter data to patient-level medical history:
 * - Diagnoses from encounters → Problem List
 * - Surgical encounter details → Surgical History
 */

import { MedicalHistoryService } from './medicalHistoryService';

class MedicalHistoryIntegrationService {
  /**
   * Sync encounter diagnoses to patient's problem list
   * @param {string} patientId - Patient ID
   * @param {string} facilityId - Facility ID
   * @param {Array} diagnoses - Array of diagnosis objects from encounter
   * @param {string} encounterId - Source encounter ID for tracking
   * @param {Date|string} encounterDate - Date of the encounter
   * @returns {Promise<Array>} Array of created/updated problems
   */
  static async syncDiagnosesToProblemList(patientId, facilityId, diagnoses, encounterId, encounterDate) {
    if (!patientId || !facilityId || !diagnoses || !Array.isArray(diagnoses) || diagnoses.length === 0) {
      console.log('MedicalHistoryIntegrationService: No diagnoses to sync');
      return [];
    }

    try {
      console.log(`MedicalHistoryIntegrationService: Syncing ${diagnoses.length} diagnoses to problem list`);
      
      // Get existing problems to check for duplicates
      const existingProblems = await MedicalHistoryService.getProblemList(patientId, facilityId);
      
      const results = [];
      
      for (const diagnosis of diagnoses) {
        // Skip if diagnosis doesn't have required fields
        if (!diagnosis.code && !diagnosis.description) {
          continue;
        }

        // Check if this diagnosis already exists (by ICD code or sourceEncounterId)
        const existingProblem = existingProblems.find(p => 
          (p.sourceEncounterId === encounterId && p.icdCode === diagnosis.code) ||
          (p.icdCode === diagnosis.code && p.condition === diagnosis.description)
        );

        if (existingProblem) {
          console.log(`MedicalHistoryIntegrationService: Diagnosis ${diagnosis.code} already exists in problem list, skipping`);
          continue;
        }

        // Map encounter diagnosis to problem list format
        const problemData = {
          condition: diagnosis.description || diagnosis.name || `Diagnosis: ${diagnosis.code}`,
          icdCode: diagnosis.code || '',
          status: 'active',
          severity: diagnosis.severity || 'moderate',
          onsetDate: this.formatDate(encounterDate),
          notes: `Added from encounter on ${this.formatDate(encounterDate)}`,
          sourceEncounterId: encounterId,
          sourceType: 'encounter_diagnosis'
        };

        try {
          const result = await MedicalHistoryService.addProblem(patientId, facilityId, problemData);
          results.push(result);
          console.log(`MedicalHistoryIntegrationService: Added problem ${diagnosis.code} to problem list`);
        } catch (error) {
          console.error(`MedicalHistoryIntegrationService: Error adding problem ${diagnosis.code}:`, error);
        }
      }

      console.log(`MedicalHistoryIntegrationService: Successfully synced ${results.length} diagnoses to problem list`);
      return results;
    } catch (error) {
      console.error('MedicalHistoryIntegrationService: Error syncing diagnoses to problem list:', error);
      throw error;
    }
  }

  /**
   * Sync surgical encounter details to patient's surgical history
   * @param {string} patientId - Patient ID
   * @param {string} facilityId - Facility ID
   * @param {Object} surgicalDetails - Surgical details from encounter
   * @param {string} encounterId - Source encounter ID for tracking
   * @param {string} facilityName - Name of the facility where surgery was performed
   * @returns {Promise<Object>} Created/updated surgical history record
   */
  static async syncSurgicalEncounterToHistory(patientId, facilityId, surgicalDetails, encounterId, facilityName = '') {
    if (!patientId || !facilityId || !surgicalDetails) {
      console.log('MedicalHistoryIntegrationService: Missing required parameters for surgical history sync');
      return null;
    }

    // Check if there's a procedure to record
    const procedureName = surgicalDetails.procedureType || surgicalDetails.procedureName || '';
    if (!procedureName) {
      console.log('MedicalHistoryIntegrationService: No procedure name found in surgical details, skipping');
      return null;
    }

    try {
      console.log(`MedicalHistoryIntegrationService: Syncing surgical encounter ${encounterId} to surgical history`);
      
      // Get existing surgical history to check for duplicates
      const existingSurgicalHistory = await MedicalHistoryService.getSurgicalHistory(patientId, facilityId);
      
      // Check if this encounter has already been synced
      const existingEntry = existingSurgicalHistory.find(s => s.sourceEncounterId === encounterId);
      
      if (existingEntry) {
        console.log(`MedicalHistoryIntegrationService: Surgical encounter ${encounterId} already exists in surgical history, skipping`);
        return existingEntry;
      }

      // Map surgical encounter details to surgical history format
      const surgicalHistoryData = {
        procedure: procedureName,
        date: this.formatDate(surgicalDetails.procedureDate || surgicalDetails.surgeryDate || new Date()),
        facility: facilityName || surgicalDetails.facility || 'Current Facility',
        surgeon: surgicalDetails.surgeon || surgicalDetails.surgeonName || '',
        notes: this.buildSurgicalNotes(surgicalDetails),
        sourceEncounterId: encounterId,
        sourceType: 'surgical_encounter'
      };

      const result = await MedicalHistoryService.addSurgicalHistory(patientId, facilityId, surgicalHistoryData);
      console.log(`MedicalHistoryIntegrationService: Successfully added surgical history for encounter ${encounterId}`);
      
      return result;
    } catch (error) {
      console.error('MedicalHistoryIntegrationService: Error syncing surgical encounter to history:', error);
      throw error;
    }
  }

  /**
   * Sync all relevant data from an encounter to medical history
   * Called when an encounter is completed or discharged
   * @param {string} patientId - Patient ID
   * @param {string} facilityId - Facility ID
   * @param {Object} encounterData - Full encounter data
   * @param {string} encounterId - Encounter ID
   * @param {string} facilityName - Facility name (optional)
   * @returns {Promise<Object>} Results of sync operations
   */
  static async syncEncounterToMedicalHistory(patientId, facilityId, encounterData, encounterId, facilityName = '') {
    const results = {
      diagnosesSynced: [],
      surgicalHistorySynced: null,
      errors: []
    };

    if (!patientId || !facilityId || !encounterData) {
      console.log('MedicalHistoryIntegrationService: Missing required parameters for sync');
      return results;
    }

    try {
      console.log(`MedicalHistoryIntegrationService: Starting sync for encounter ${encounterId}`);

      // 1. Sync diagnoses to problem list
      const diagnoses = encounterData.diagnoses || [];
      if (diagnoses.length > 0) {
        try {
          const encounterDate = encounterData.createdAt || encounterData.startDate || new Date();
          results.diagnosesSynced = await this.syncDiagnosesToProblemList(
            patientId, 
            facilityId, 
            diagnoses, 
            encounterId,
            encounterDate
          );
        } catch (error) {
          console.error('MedicalHistoryIntegrationService: Error syncing diagnoses:', error);
          results.errors.push({ type: 'diagnoses', error: error.message });
        }
      }

      // 2. Sync surgical details to surgical history (for surgical encounters)
      const encounterType = encounterData.type || encounterData.encounterType || '';
      const isSurgicalEncounter = encounterType.toLowerCase().includes('surg') || 
                                   encounterType === 'surgery' ||
                                   encounterData.surgicalDetails;
      
      if (isSurgicalEncounter && encounterData.surgicalDetails) {
        try {
          results.surgicalHistorySynced = await this.syncSurgicalEncounterToHistory(
            patientId,
            facilityId,
            encounterData.surgicalDetails,
            encounterId,
            facilityName
          );
        } catch (error) {
          console.error('MedicalHistoryIntegrationService: Error syncing surgical history:', error);
          results.errors.push({ type: 'surgicalHistory', error: error.message });
        }
      }

      console.log('MedicalHistoryIntegrationService: Sync completed', {
        diagnosesCount: results.diagnosesSynced.length,
        surgicalHistorySynced: !!results.surgicalHistorySynced,
        errorsCount: results.errors.length
      });

      return results;
    } catch (error) {
      console.error('MedicalHistoryIntegrationService: Error during encounter sync:', error);
      results.errors.push({ type: 'general', error: error.message });
      return results;
    }
  }

  /**
   * Build notes string from surgical details
   * @param {Object} surgicalDetails - Surgical details object
   * @returns {string} Formatted notes string
   */
  static buildSurgicalNotes(surgicalDetails) {
    const notes = [];
    
    if (surgicalDetails.preOpAssessment) {
      notes.push(`Pre-Op Assessment: ${surgicalDetails.preOpAssessment}`);
    }
    
    if (surgicalDetails.postOpInstructions) {
      notes.push(`Post-Op Instructions: ${surgicalDetails.postOpInstructions}`);
    }
    
    if (surgicalDetails.anesthesiologist) {
      notes.push(`Anesthesiologist: ${surgicalDetails.anesthesiologist}`);
    }
    
    if (surgicalDetails.operatingRoom) {
      notes.push(`Operating Room: ${surgicalDetails.operatingRoom}`);
    }
    
    if (surgicalDetails.estimatedDuration) {
      notes.push(`Duration: ${surgicalDetails.estimatedDuration}`);
    }

    if (surgicalDetails.followUpRequired) {
      notes.push(`Follow-up Required: ${surgicalDetails.followUpDate || 'Yes'}`);
    }

    return notes.join('\n') || 'Synced from encounter';
  }

  /**
   * Format date to ISO string (YYYY-MM-DD)
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted date string
   */
  static formatDate(date) {
    if (!date) {
      return new Date().toISOString().split('T')[0];
    }
    
    if (typeof date === 'string') {
      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      // Try to parse as date
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0];
      }
      return new Date().toISOString().split('T')[0];
    }
    
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    
    // Handle Firestore Timestamp objects
    if (date.seconds) {
      return new Date(date.seconds * 1000).toISOString().split('T')[0];
    }
    
    if (date.toDate && typeof date.toDate === 'function') {
      return date.toDate().toISOString().split('T')[0];
    }
    
    return new Date().toISOString().split('T')[0];
  }
}

export default MedicalHistoryIntegrationService;
